import React from "react";
import { motion } from "framer-motion";
import "../styles/manifesto.css";

// Reusable fade-up variant (matches HomePage.js)
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
  },
};

const SYSTEMS = [
  {
    name: "Governance",
    before: "Representative democracy prone to corruption and capture",
    after:
      "Direct participation, facilitated by technology, rooted in collective wisdom",
  },
  {
    name: "Education",
    before: "Industrial uniformity — producing compliant workers",
    after:
      "Discovery of unique gifts, personalized paths, curiosity as the engine",
  },
  {
    name: "Health care",
    before: "Treatment of disease after breakdown",
    after:
      "Prevention, wholeness, and the cultivation of vitality across a lifetime",
  },
  {
    name: "Business",
    before: "Profit as the only legitimate measure of value",
    after: "Positive impact as the mandate — organizations as stewards of life",
  },
  {
    name: "Finance",
    before: "Capital accumulation and speculation at any cost",
    after: "Capital as a regenerative force — investment as an act of care",
  },
];

const TEAMS = [
  {
    role: "Pathway One",
    name: "Flow Journey",
    body: "Flow is not a performance state. It is the natural condition of a being fully aligned with their purpose and their present moment. Flow Journey accompanies leaders and individuals into this alignment — dissolving the inner obstacles of fear, self-doubt, and conditioned limitation — so that their action arises from a place of genuine intelligence and creative power.",
  },
  {
    role: "Pathway Two",
    name: "Quantum Vision",
    body: "Before a system can be redesigned, it must be seen differently. Quantum Vision expands the perceptual field of leaders — opening them to the participatory nature of reality, to the non-dual dimensions of awareness that underlie all phenomena. When a leader can perceive the quantum field of possibility underlying any given situation, they become capable of solutions that the conditioned mind cannot generate.",
  },
  {
    role: "Pathway Three",
    name: "Synergistic Intelligence",
    body: "Intelligence becomes synergistic when it recognizes that the whole is not merely the sum of its parts — it is an emergent reality of an entirely different order. The Synergistic Intelligence team works at the level of systems design, helping organizations, institutions, and communities build structures that reflect the intelligence of life itself: adaptive, regenerative, coherent, and deeply attuned to the wellbeing of all who inhabit them.",
  },
];

function Article({ num, title, children, alt }) {
  return (
    <motion.article
      className={`manifesto-article${alt ? " manifesto-article--alt" : ""}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.25 }}
      variants={fadeUp}
    >
      <div className="manifesto-article-inner">
        <div className="manifesto-article-header">
          <span className="section-label manifesto-article-num">{num}</span>
          <h2 className="manifesto-article-title">{title}</h2>
        </div>
        <div className="manifesto-article-body">{children}</div>
      </div>
    </motion.article>
  );
}

export default function Manifesto() {
  return (
    <>
      {/* ── COVER ── */}
      <header className="manifesto-cover">
        <div className="manifesto-cover-border" aria-hidden="true" />
        <p className="manifesto-cover-ornament">A Founding Vision</p>
        <h1 className="manifesto-cover-title">
          Synergistic
          <br />
          <em>Intelligence</em>
        </h1>
        <p className="manifesto-cover-subtitle">For a Benevolent World</p>
        <div className="manifesto-cover-rule" />
        <p className="manifesto-cover-declaration">
          We hold as self-evident that every living being carries inherent
          worth — and that every system we build together must begin from
          that truth.
        </p>
      </header>

      {/* ── MANIFESTO BODY ── */}
      <main className="manifesto-main">
        {/* Preamble */}
        <div className="manifesto-preamble">
          <span className="section-label manifesto-preamble-label">Preamble</span>
          <p className="manifesto-preamble-text">
            The great systems of human civilization — government, education,
            health, business, finance — were built on a foundational
            assumption: that <strong>human nature is dangerous and must be
            controlled.</strong>
          </p>
          <p className="manifesto-preamble-text">
            We reject this premise. Not naively, but with clear eyes — and
            with a vision of what becomes possible when we build from a
            different premise entirely.
          </p>
        </div>

        {/* Article I */}
        <Article num="Article I" title="On the Nature of Being">
          <p>
            Every living being — every person, creature, ecosystem — carries
            inherent, irreducible worth. This is not a sentiment. It is the
            operational foundation of everything we do.
          </p>
          <p>
            The punitive model of civilization assumes a sinful or deficient
            human nature that must be corrected, managed, and contained.
            Synergistic Intelligence begins instead from the radical
            assumption that <em>life is intelligently designed toward
            flourishing</em> — and that the primary work of any institution
            is to remove the obstacles that prevent that flourishing from
            expressing itself.
          </p>
          <p>
            When we govern from this premise, education transforms. When we
            educate from this premise, health transforms. When we build
            business from this premise, the economy transforms. The premise
            is everything.
          </p>
        </Article>

        {/* Article II */}
        <Article num="Article II" title="On the Quantum Nature of Reality" alt>
          <p>
            Modern physics has revealed something the ancient wisdom
            traditions always knew: reality is not a fixed, mechanical
            system that operates independently of the observer. It is a
            participatory field — shaped by attention, intention, and
            consciousness.
          </p>
          <p>
            Wheeler's participatory universe tells us that observers do not
            merely witness reality — they are constitutive of it. The
            Copenhagen interpretation confirms that at the most fundamental
            level, matter is potential until it is observed. This is not
            metaphor. This is physics.
          </p>
          <p>
            The implications are staggering. If consciousness participates
            in the creation of reality, then{" "}
            <em>
              the interior condition of leaders and citizens is not separate
              from the systems they govern
            </em>{" "}
            — it is the source of those systems. This is why transformation
            that begins only at the level of policy and law always fails. It
            does not go deep enough.
          </p>
          <p>We go deeper.</p>
        </Article>

        {/* Systems transformation */}
        <motion.div
          className="manifesto-systems"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <span className="section-label manifesto-systems-label">
            Article III — The Quantum Leap in System Design
          </span>
          <div className="manifesto-systems-grid">
            {SYSTEMS.map((row) => (
              <div className="manifesto-system-row" key={row.name}>
                <span className="manifesto-system-name">{row.name}</span>
                <span className="manifesto-system-old">{row.before}</span>
                <span className="manifesto-system-new">{row.after}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Article IV */}
        <Article num="Article IV" title="On the Role of Leaders" alt>
          <p>
            We begin with leaders — not because they are more worthy, but
            because they are positioned at the leverage points of systems. A
            leader who operates from fear, scarcity, or unconscious
            conditioning will reproduce those qualities in every institution
            they touch, no matter how good their intentions.
          </p>
          <p>
            A leader who operates from flow — from a deep alignment of
            purpose, awareness, and creative intelligence — becomes a
            generative force. They do not merely manage systems. They{" "}
            <em>transmute</em> them.
          </p>
          <p>
            This is why the inner work is not separate from the outer work.
            It is the prerequisite for it. Synergistic Intelligence
            accompanies leaders through the threshold of their own
            transformation — because on the other side of that threshold,
            they become capable of building things that have never been
            built before.
          </p>
        </Article>

        <div className="manifesto-transition-block">
          <span className="section-label manifesto-transition-label">Our Approach</span>
          <p>Leaders first. Then all beings. The ripple does not stop.</p>
        </div>

        {/* SI Teams */}
        <motion.div
          className="manifesto-teams"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <span className="section-label manifesto-teams-label">
            Article V — Three Pathways of Transformation
          </span>
          {TEAMS.map((team) => (
            <div className="manifesto-team-entry" key={team.name}>
              <span className="manifesto-team-entry-role">{team.role}</span>
              <h3 className="manifesto-team-entry-name">{team.name}</h3>
              <p className="manifesto-team-entry-body">{team.body}</p>
            </div>
          ))}
        </motion.div>

        {/* Article VI */}
        <Article num="Article VI" title="On the World We Are Building">
          <p>
            We are not utopians. We do not believe transformation happens
            all at once, or without friction, or without the hard and
            patient work of cultural and institutional change.
          </p>
          <p>
            But we do believe — with evidence, with philosophy, and with the
            testimony of every wisdom tradition on earth — that{" "}
            <em>the direction of change is not arbitrary.</em> Life moves
            toward coherence. Consciousness expands. Systems that serve life
            tend to persist; systems that exploit it tend to collapse.
          </p>
          <p>We are working with the direction of history, not against it.</p>
          <p>
            Our vision is of a world where every child discovers what they
            were born to contribute. Where every leader governs from wisdom
            rather than fear. Where every institution exists to serve all
            life rather than to extract from it. Where the default
            assumption about every human being is not suspicion — but
            potential.
          </p>
          <p>
            This is the world Synergistic Intelligence is building, one
            leader, one organization, one community, one quantum leap at a
            time.
          </p>
        </Article>

        {/* Closing */}
        <motion.div
          className="manifesto-closing"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUp}
        >
          <span className="section-label manifesto-closing-ornament">The Commitment</span>
          <p className="manifesto-closing-text">
            "We commit to the flourishing of all beings — not as an
            aspiration we work toward, but as the ground we stand on."
          </p>
          <div className="manifesto-closing-rule" />
          <p className="manifesto-sig">Synergistic Intelligence</p>
        </motion.div>
      </main>
    </>
  );
}